import React from 'react';
import { Typography, Box, Grid, Paper, useTheme, alpha, Rating, Avatar, Chip } from '@mui/material';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';

const rankColors = ['#FFD700', '#C0C0C0', '#CD7F32'];

function StatWidget({ icon, label, value, color, children }) {
  const theme = useTheme();
  return (
    <Paper elevation={0} sx={{
      p: 3,
      borderRadius: 3,
      border: `1px solid ${theme.palette.divider}`,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'box-shadow 0.2s ease, transform 0.2s ease',
      '&:hover': {
        boxShadow: `0 4px 20px ${alpha(color, 0.12)}`,
        transform: 'translateY(-2px)',
      },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(color, 0.1), color,
          flexShrink: 0,
        }}>
          {icon}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          {label}
        </Typography>
      </Box>
      {children || (
        <Typography variant="h4" sx={{ fontWeight: 800, color, lineHeight: 1, mt: 'auto' }}>
          {value}
        </Typography>
      )}
    </Paper>
  );
}

function ListWidget({ icon, label, color, items, valueKey = 'chatCount', nameKey = 'agentName', emptyMsg = 'No data' }) {
  const theme = useTheme();
  return (
    <Paper elevation={0} sx={{
      p: 3,
      borderRadius: 3,
      border: `1px solid ${theme.palette.divider}`,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'box-shadow 0.2s ease, transform 0.2s ease',
      '&:hover': {
        boxShadow: `0 4px 20px ${alpha(color, 0.12)}`,
        transform: 'translateY(-2px)',
      },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(color, 0.1), color,
          flexShrink: 0,
        }}>
          {icon}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          {label}
        </Typography>
      </Box>
      {items && items.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {items.map((item, idx) => (
            <Box key={item[nameKey] || idx} sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              p: 0.75, px: 1, borderRadius: 1.5,
              bgcolor: idx === 0 ? alpha(color, 0.04) : 'transparent',
              '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.06) },
            }}>
              <Avatar sx={{
                width: 24, height: 24, fontSize: '0.7rem', fontWeight: 700,
                bgcolor: idx < 3 ? alpha(rankColors[idx], 0.2) : alpha(theme.palette.text.secondary, 0.1),
                color: idx < 3 ? rankColors[idx] : theme.palette.text.secondary,
              }}>
                {idx + 1}
              </Avatar>
              <Typography variant="body2" sx={{
                flex: 1, fontWeight: idx === 0 ? 600 : 400,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                fontSize: '0.8rem',
              }}>
                {item[nameKey] || 'Unknown'}
              </Typography>
              <Chip
                label={item[valueKey]?.toLocaleString() || 0}
                size="small"
                sx={{
                  height: 22, fontWeight: 700, fontSize: '0.7rem',
                  bgcolor: alpha(color, 0.08), color,
                }}
              />
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 'auto' }}>{emptyMsg}</Typography>
      )}
    </Paper>
  );
}

function formatStorage(val) {
  if (!val) return '--';
  const match = String(val).match(/^([\d.]+)\s*(.*)$/);
  if (!match) return val;
  const num = parseFloat(match[1]);
  return `${num.toLocaleString()} ${match[2]}`.trim();
}

export default function AnalyticsWidgets({ widgets }) {
  const theme = useTheme();

  return (
    <Grid container spacing={3} justifyContent="center">
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <StatWidget
          icon={<StorageRoundedIcon />}
          label="Storage"
          value={formatStorage(widgets?.storageSize)}
          color={theme.palette.info.main}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <StatWidget
          icon={<TimerOutlinedIcon />}
          label="Avg. Chat Duration"
          color={theme.palette.warning.main}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 'auto' }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.warning.main, lineHeight: 1 }}>
              {widgets?.avgChatDurationMin != null ? widgets.avgChatDurationMin.toFixed(1) : '--'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>min</Typography>
          </Box>
        </StatWidget>
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <StatWidget
          icon={<StarOutlineRoundedIcon />}
          label="Average Chat Rating"
          color="#f59e0b"
        >
          <Box sx={{ mt: 'auto' }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#f59e0b', lineHeight: 1, mb: 1 }}>
              {widgets?.avgChatRating != null ? widgets.avgChatRating.toFixed(2) : '--'}
            </Typography>
            <Rating value={widgets?.avgChatRating || 0} precision={0.1} readOnly size="medium" sx={{ fontSize: 24 }} />
          </Box>
        </StatWidget>
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <StatWidget
          icon={<ChatBubbleOutlineRoundedIcon />}
          label="Avg. Messages / Chat"
          value={widgets?.avgMessagesPerChat != null ? widgets.avgMessagesPerChat.toFixed(1) : '--'}
          color={theme.palette.success.main}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <ListWidget
          icon={<EmojiEventsRoundedIcon />}
          label="Top 5 Agents"
          color={theme.palette.primary.main}
          items={widgets?.topAgents}
          valueKey="chatCount"
          nameKey="agentName"
          emptyMsg="No agent data"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <ListWidget
          icon={<SmartToyOutlinedIcon />}
          label="Top 5 Models"
          color={theme.palette.secondary.main || '#9c27b0'}
          items={widgets?.topModels}
          valueKey="chatCount"
          nameKey="modelName"
          emptyMsg="No model data"
        />
      </Grid>
    </Grid>
  );
}
